/**
 * Pixel-font labels for the DOM chrome, one per text tier (core/hud/type.ts).
 *
 * The text is rasterized from the bundle's HUD font atlas (`textMask`) into a small canvas at
 * `n` device px per glyph px (`tierRun`) and shown at CSS size device / dpr with
 * `image-rendering: pixelated`, so it is exactly as crisp as the HUD. Before the fonts load (or if they fail) the label falls back to CSS monospace text.
 */
import type { RGB } from '../core/api.ts';
import { type PixelFont, type PixelFonts, textMask } from '../core/hud/font.ts';
import { type TextSize, tierRun } from '../core/hud/type.ts';

export interface ChromeScale {
  /** device px per stage px (f) */
  k: number;
  dpr: number;
}

export class PixelLabel {
  readonly el: HTMLSpanElement;
  private readonly canvas: HTMLCanvasElement;
  private key = '';

  constructor(
    private readonly tier: TextSize = 'M',
    className = 'px-label',
  ) {
    this.el = document.createElement('span');
    this.el.className = className;
    this.canvas = document.createElement('canvas');
    this.canvas.setAttribute('aria-hidden', 'true');
  }

  /** Draw `text` in `rgb`; a no-op when nothing changed. */
  set(text: string, rgb: RGB, fonts: PixelFonts | null, sc: ChromeScale): void {
    const key = `${text}|${rgb.join(',')}|${fonts ? '1' : '0'}|${String(sc.k)}|${String(sc.dpr)}`;
    if (key === this.key) return;
    this.key = key;
    this.el.setAttribute('aria-label', text);
    this.el.dataset.text = text;
    if (!fonts) {
      this.el.textContent = text;
      this.el.classList.add('px-fallback');
      this.el.style.color = `rgb(${rgb.join(',')})`;
      return;
    }
    this.el.classList.remove('px-fallback');
    if (this.canvas.parentNode !== this.el) {
      this.el.textContent = '';
      this.el.appendChild(this.canvas);
    }
    const run = tierRun(this.tier, sc.k);
    drawPixelText(this.canvas, fonts[run.font], text, run.n, rgb, sc.dpr);
  }
}

/** Rasterize `text` into `canvas` at `n` device px per glyph px and size it in CSS px (1:1 device px). */
export function drawPixelText(canvas: HTMLCanvasElement, font: PixelFont, text: string, n: number, rgb: RGB, dpr: number): void {
  const m = textMask(font, text, 1, n);
  const w = Math.max(1, m.w);
  const h = Math.max(1, m.h);
  canvas.width = w;
  canvas.height = h;
  canvas.style.width = `${String(w / dpr)}px`;
  canvas.style.height = `${String(h / dpr)}px`;
  canvas.style.imageRendering = 'pixelated';
  const ctx = canvas.getContext('2d');
  if (!ctx || m.w === 0) return;
  const img = ctx.createImageData(w, h);
  const d = img.data;
  for (let i = 0; i < m.data.length; i++) {
    if (m.data[i] !== 1) continue;
    const o = i * 4;
    d[o] = rgb[0];
    d[o + 1] = rgb[1];
    d[o + 2] = rgb[2];
    d[o + 3] = 255;
  }
  ctx.putImageData(img, 0, 0);
}
